function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import cn from 'classnames';
import { preventBadNumberInputKeys, preventBadNumberInputValues } from "../../../input-management";
import { Tooltip } from "../../Tooltip";
import { Typography } from "../../Typography";
import { TableList } from "../TableList";
import styles from "./InlineControl.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const InlineInput = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    value,
    error,
    errorText,
    errorTextInTooltip,
    inputProps,
    testId,
    className,
    onChange,
    onFocus,
    onBlur,
    onSave
  } = props;
  const inputRef = useRef(null);

  // workaround to reuse the same ref
  useImperativeHandle(ref, () => {
    var _inputRef$current;
    return (_inputRef$current = inputRef.current) !== null && _inputRef$current !== void 0 ? _inputRef$current : new HTMLInputElement();
  });

  // The initial value when focusing on the input.
  const [pristineValue, setPristineValue] = useState(value);

  // Logic for blur on {enter} and undo on {escape}.
  useEffect(() => {
    const inputEl = inputRef.current;
    const onKeyDown = event => {
      if (!inputEl || document.activeElement !== inputEl) {
        return;
      }
      if (event.key === 'Enter') {
        inputEl.blur();
      } else if (event.key === 'Escape') {
        onChange === null || onChange === void 0 || onChange(pristineValue);
        inputEl.blur();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onChange, pristineValue]);

  // Save mechanism
  const [valueToSave, setValueToSave] = useState(null);

  // should be called after validation process has completed
  useEffect(() => {
    if (valueToSave !== null) {
      setValueToSave(null);
      if (!error && valueToSave !== pristineValue) {
        setPristineValue(valueToSave);
        onSave === null || onSave === void 0 || onSave(valueToSave);
      }
    }
  }, [error, onSave, pristineValue, valueToSave]);
  const isNumberInput = inputProps && inputProps.type === 'number';
  return /*#__PURE__*/_jsx(Tooltip, {
    title: errorTextInTooltip && errorText ? /*#__PURE__*/_jsx("div", {
      "data-testid": testId && "".concat(testId, "-error"),
      children: errorText
    }) : '',
    interactive: true,
    disableArrow: true,
    disableHoverListener: true,
    disableTouchListener: true,
    children: /*#__PURE__*/_jsxs("div", {
      className: cn(styles.root, className),
      children: [/*#__PURE__*/_jsx("div", {
        className: cn(styles.frame, styles.absolutePosition, TableList.classes.showOnRowHover)
      }), /*#__PURE__*/_jsx(Typography, _objectSpread(_objectSpread({}, inputProps), {}, {
        ellipsis: true,
        as: "input",
        ref: inputRef,
        weight: "normal",
        className: cn(styles.formElement, styles.absolutePosition, {
          [styles.error]: error
        }),
        value: value,
        testId: testId,
        "data-test-error": error || undefined,
        tooltipProps: {
          disableFocusListener: true
        },
        onChange: function (_onChange) {
          function onChange(_x) {
            return _onChange.apply(this, arguments);
          }
          onChange.toString = function () {
            return _onChange.toString();
          };
          return onChange;
        }(e => {
          const value = e.currentTarget.value;
          const inFocus = document.activeElement === e.currentTarget;
          isNumberInput && preventBadNumberInputValues(e);
          onChange === null || onChange === void 0 || onChange(value);
          if (!inFocus) {
            setValueToSave(value.trim());
          }
        }),
        onKeyDown: isNumberInput ? e => preventBadNumberInputKeys(e, inputProps.min != null && +inputProps.min >= 0) : undefined,
        onFocus: function (_onFocus) {
          function onFocus(_x2) {
            return _onFocus.apply(this, arguments);
          }
          onFocus.toString = function () {
            return _onFocus.toString();
          };
          return onFocus;
        }(e => {
          onFocus === null || onFocus === void 0 || onFocus(e);
        }),
        onBlur: function (_onBlur) {
          function onBlur(_x3) {
            return _onBlur.apply(this, arguments);
          }
          onBlur.toString = function () {
            return _onBlur.toString();
          };
          return onBlur;
        }(e => {
          onBlur === null || onBlur === void 0 || onBlur(e);
          setValueToSave(e.currentTarget.value.trim());
        })
      })), errorText && !errorTextInTooltip && /*#__PURE__*/_jsx(Typography, {
        variant: "paragraph3",
        skin: "error",
        className: styles.errorText,
        testId: testId && "".concat(testId, "-error"),
        children: errorText
      })]
    })
  });
});